import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Form,
  Table,
  TableColumnsType,
} from "antd";
import { HttpStatusCode } from "axios";
import dayjs from "dayjs";
import { toast } from "sonner";
import style from "../style.module.scss";
import { SimpleInput } from "../../../common/components/simpleInput";
import { InputSelect } from "../../../common/components/InputSelect/InputSelect";
import { API_URLs } from "../../../utilities/API_URLs";
import { getIdFromIri } from "../../../common/utils/iri";
import { ReleaseOrderService } from "../store/_requests";
import { RELEASE_ORDER_CONFIG } from "../core/_constant.releaseOrder";
import {
  CreateReleaseOrderPayload,
  PendingRemovalData,
  RemovalType,
} from "../core/_model.releaseOrder";

type CreateReleaseOrderFormProps = {
  removalType: RemovalType;
  onCreated: () => void;
  onClose: () => void;
};

const getRemovalIris = (items: PendingRemovalData[]): string[] =>
  items.filter((item) => !!item?.removal).map((item) => item.removal as string);

export const CreateReleaseOrderForm = ({
  removalType,
  onCreated,
  onClose,
}: CreateReleaseOrderFormProps) => {
  const [form] = Form.useForm();
  const config = RELEASE_ORDER_CONFIG[removalType];

  const customer = Form.useWatch("customer", form);
  const exitDate = Form.useWatch("exitDate", form);
  const booking = Form.useWatch("booking", form);

  const [pendingRemovals, setPendingRemovals] = useState<PendingRemovalData[]>(
    []
  );
  const [selectedRemovals, setSelectedRemovals] = useState<string[]>([]);
  const [isLoadingPending, setIsLoadingPending] = useState<boolean>(false);
  const [hasLoadedPending, setHasLoadedPending] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // InputSelect works with the IRI as value, the API expects the numeric id.
  const customerId = getIdFromIri(customer?.value);
  const exitDateIso = exitDate ? dayjs(exitDate).format("YYYY-MM-DD") : null;
  const bookingId = booking?.value ?? booking?.id ?? booking;
  const canLoadPendingRemovals = !!customerId && !!exitDateIso && (removalType !== "CONTAINER" || !!bookingId);;

  useEffect(() => {
    let isStale = false;

    const fetchPendingRemovals = async () => {
      if (
        !customerId ||
        !exitDateIso ||
        (removalType === "CONTAINER" && !bookingId)
      ) {
        setPendingRemovals([]);
        setSelectedRemovals([]);
        setHasLoadedPending(false);
        setIsLoadingPending(false);
        return;
      }

      try {
        setIsLoadingPending(true);

        const res = await ReleaseOrderService.getPendingRemovals({
          customer: customerId,
          date: exitDateIso,
          removalType,
          ...(removalType === "CONTAINER" ? { booking: bookingId } : {}),
        });

        if (isStale) return;

        const member: PendingRemovalData[] = Array.isArray(res?.data?.member)
          ? res.data.member
          : [];

        setPendingRemovals(member);
        setSelectedRemovals(getRemovalIris(member));
        setHasLoadedPending(true);
      } catch (error) {
        console.log(error);

        if (!isStale) {
          setPendingRemovals([]);
          setSelectedRemovals([]);
          setHasLoadedPending(true);
        }
      } finally {
        if (!isStale) {
          setIsLoadingPending(false);
        }
      }
    };

    fetchPendingRemovals();

    return () => {
      isStale = true;
    };
  }, [customerId, exitDateIso, removalType, bookingId]);

  const hasValueInResults = (key: keyof PendingRemovalData) =>
    pendingRemovals.some((item) => {
      const value = item?.[key];
      return value !== null && value !== undefined && value !== "";
    });

  const buildColumns = (): TableColumnsType<any> => {
    const columns: TableColumnsType<any> = [
      {
        key: "reference",
        title: "Référence",
        dataIndex: "reference",
        width: 125,
      },
      {
        key: "identifier",
        title: "Identifiant",
        dataIndex: "identifier",
       width: 125,
        render: (value: any) => value ?? "-",
      },
    ];

    // Les colonnes sans aucune valeur pour cette sélection sont masquées : un
    // enlèvement de marchandises n'a ni plomb ni BL, un conteneur n'a pas de sacs.
    const optionalColumns: Array<[keyof PendingRemovalData, string]> = [
      ["designation", "Désignation"],
      ["tonnage", "Tonnage"],
      ["sealNumber", "N° Plomb"],
      ["blNumber", "N° BL"],
      ["bagCount", "Nb sacs"],
      ["vessel", "Navire"],
      ["exitPoint", "Point de sortie"],
      ["truckNumber", "Camion"],
      ["driverName", "Conducteur"],
    ];

    optionalColumns.forEach(([key, title]) => {
      if (hasValueInResults(key)) {
        columns.push({
          key: String(key),
          title,
          dataIndex: String(key),
          width: 125,
          render: (value: any) => value ?? "-",
        });
      }
    });

    return columns;
  };

  const allSelected =
    pendingRemovals.length > 0 &&
    selectedRemovals.length === pendingRemovals.length;
  const isIndeterminate = selectedRemovals.length > 0 && !allSelected;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedRemovals.length) {
        toast.error(
          `Sélectionnez au moins un ${config.removalLabel} à inclure dans le bon de sortie.`
        );
        return;
      }

      setIsSubmitting(true);

      const payload: CreateReleaseOrderPayload = {
        customer: values.customer?.value,
        exitDate: dayjs(values.exitDate).format("YYYY-MM-DD"),
        removals: selectedRemovals,
      };

      const res = await ReleaseOrderService.createReleaseOrder(payload);

      if (res?.status === HttpStatusCode.Created) {
        form.resetFields();
        setPendingRemovals([]);
        setSelectedRemovals([]);
        onCreated();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className={style.createReleaseOrder__form}
      initialValues={{ exitDate: dayjs() }}
    >
      <div className={style.createReleaseOrder__fields}>
        <div className={style.createReleaseOrder__row}>
          <div
            className={`${style.createReleaseOrder__field} ${
              removalType !== "CONTAINER"
                ? style.createReleaseOrder__fieldFull
                : ""
            }`}
          >
            <SimpleInput
          Form={Form}
          label={`Transitaire ${removalType === "GOODS" ? ("/ Client") : ("")}`}
          name="customer"
          rules={[
            {
              required: true,
              message: "Veuillez sélectionner un client",
            },
          ]}
          customInput={
            <InputSelect
              uri={API_URLs.settings.customers.getCustomerSelect}
              onChange={() => {
                form.setFieldsValue({ booking: undefined });
                setPendingRemovals([]);
                setSelectedRemovals([]);
                setHasLoadedPending(false);
              }}
            />
          }
        />
          </div>
  
          {removalType === "CONTAINER" && (
            <div className={style.createReleaseOrder__field}>
              <SimpleInput
            Form={Form}
            label="Réservation (booking)"
            name="booking"
            rules={[
              {
                required: true,
                message: "Veuillez sélectionner une réservation",
              },
            ]}
            customInput={
              <InputSelect
                uri={`${API_URLs.booking.bookingSelect}?customer=${customerId}`}
                disabled={!customerId}
                onChange={() => {}}
                valueOption={{
                  labelKey: (item: any) => item?.referenceLabel,
                  valueKey: "id",
                }}
              />
            }
          />
            </div>
          )}
        </div>
  
        <div className={style.createReleaseOrder__row}>
          <div className={style.createReleaseOrder__field}>
            <SimpleInput
              Form={Form}
              label="Date de sortie"
              name="exitDate"
              rules={[
                {
                  required: true,
                  message: "Veuillez sélectionner une date de sortie",
                },
              ]}
              customInput={
                <DatePicker
                  className="w-100"
                  format="DD/MM/YYYY"
                  placeholder="JJ/MM/AAAA"
                  disabledDate={(current) =>
                    !!current && current > dayjs().endOf("day")
                  }
                />
              }
            />
          </div>
        </div>
      </div>

      <div className={style.pendingRemovals}>
        <div className={style.pendingRemovals__header}>
          <div>
            <div className={style.pendingRemovals__title}>
              Enlèvements à inclure
            </div>
  
            {hasLoadedPending && pendingRemovals.length > 0 && (
              <div className={style.pendingRemovals__counter}>
                {pendingRemovals.length} en attente ·{" "}
                {selectedRemovals.length} sélectionné(s)
              </div>
            )}
          </div>
  
          {hasLoadedPending && pendingRemovals.length > 0 && (
            <Checkbox
              checked={allSelected}
              indeterminate={isIndeterminate}
              onChange={(e) =>
                setSelectedRemovals(
                  e.target.checked ? getRemovalIris(pendingRemovals) : []
                )
              }
            >
              Tout sélectionner
            </Checkbox>
          )}
        </div>

        {!canLoadPendingRemovals ? (
          <div className={style.pendingRemovals__hint}>
            {config.pendingRemovalsHint}
          </div>
        ) : hasLoadedPending && pendingRemovals.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`Aucun ${config.removalLabel} en attente pour ce client à cette date.`}
          />
        ) : (
          <Table
            className={style.pendingRemovals__table}
            rowKey={(record: PendingRemovalData) =>
              record.removal ?? record.reference ?? ""
            }
            columns={buildColumns().map((column) => ({
              ...column,
              align: "center" as const,
            }))}
            dataSource={pendingRemovals}
            loading={isLoadingPending}
            size="small"
            pagination={false}
            tableLayout="fixed"
            scroll={{
              x: "max-content",
              y: 320,
            }}
            rowSelection={{
              columnWidth: 48,
              selectedRowKeys: selectedRemovals,
              onChange: (keys) =>
                setSelectedRemovals(keys.map((key) => String(key))),
            }}
          />
        )}
      </div>

      <div className={style.createReleaseOrder__footer}>
        <Button onClick={onClose} disabled={isSubmitting}>
          Annuler
        </Button>
  
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!selectedRemovals.length}
        >
          Enregistrer
        </Button>
      </div>
    </Form>
  );
};
